import React, { useState, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./ModernDatePicker.css";
import { MdOutlineDateRange } from "react-icons/md";
import { parseCustomDate } from "../../utils/dateUtils";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const YEARS_PER_PAGE = 12; // 4 rows × 3 cols

const ModernDatePicker = ({
  value,
  onChange,
  placeholder = "Select date",
  dateFormat = "dd/MM/yyyy",
  maxDate,
  minDate,
  showYearNav = true,
  inputClassName = "",
}) => {
  const [showYearGrid, setShowYearGrid] = useState(false);
  const [yearPageStart, setYearPageStart] = useState(null);
  const changeYearRef = useRef(null);

  const maxYear = maxDate ? maxDate.getFullYear() : new Date().getFullYear();
  const minYear = minDate ? minDate.getFullYear() : maxYear - 100;

  const openYearGrid = (currentYear) => {
    // Align page so currentYear is visible
    const offset = currentYear - minYear;
    const page = Math.floor(offset / YEARS_PER_PAGE) * YEARS_PER_PAGE;
    setYearPageStart(minYear + page);
    setShowYearGrid(true);
  };

  const closeYearGrid = () => setShowYearGrid(false);

  const renderCustomHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => {
    changeYearRef.current = changeYear;
    const currentYear = date.getFullYear();

    return (
      <div className="mdp-header">
        <button
          type="button"
          className="mdp-nav-btn"
          onClick={showYearGrid ? () => {
            const prev = (yearPageStart ?? currentYear) - YEARS_PER_PAGE;
            if (prev >= minYear) setYearPageStart(prev);
          } : decreaseMonth}
          disabled={showYearGrid
            ? (yearPageStart ?? minYear) <= minYear
            : prevMonthButtonDisabled}
          aria-label={showYearGrid ? "Previous years" : "Previous month"}
        >
          ‹
        </button>

        <div className="mdp-selects">
          {/* Month select — hidden when year grid is open */}
          {!showYearGrid && (
            <select
              className="mdp-select"
              value={MONTHS[date.getMonth()]}
              onChange={({ target: { value: v } }) => changeMonth(MONTHS.indexOf(v))}
            >
              {MONTHS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          )}

          {showYearNav && (
            <button
              type="button"
              className={`mdp-year-btn ${showYearGrid ? "mdp-year-btn--active" : ""}`}
              onClick={() => showYearGrid ? closeYearGrid() : openYearGrid(currentYear)}
            >
              {showYearGrid
                ? `${yearPageStart ?? currentYear} – ${Math.min((yearPageStart ?? currentYear) + YEARS_PER_PAGE - 1, maxYear)}`
                : `${currentYear} ${showYearGrid ? "▲" : "▼"}`}
            </button>
          )}
        </div>

        <button
          type="button"
          className="mdp-nav-btn"
          onClick={showYearGrid ? () => {
            const next = (yearPageStart ?? currentYear) + YEARS_PER_PAGE;
            if (next <= maxYear) setYearPageStart(next);
          } : increaseMonth}
          disabled={showYearGrid
            ? (yearPageStart ?? minYear) + YEARS_PER_PAGE > maxYear
            : nextMonthButtonDisabled}
          aria-label={showYearGrid ? "Next years" : "Next month"}
        >
          ›
        </button>
      </div>
    );
  };

  // Overlay rendered inside the calendar container when grid is open
  const YearGrid = ({ selectedYear }) => {
    const start = yearPageStart ?? Math.max(minYear, maxYear - YEARS_PER_PAGE + 1);
    const end = Math.min(start + YEARS_PER_PAGE - 1, maxYear);
    const gridYears = [];
    for (let y = start; y <= end; y++) gridYears.push(y);

    return (
      <div className="mdp-year-grid-overlay">
        <div className="mdp-year-grid-divider" />
        <div className="mdp-year-grid">
          {gridYears.map((y) => (
            <button
              key={y}
              type="button"
              className={`mdp-year-cell ${y === selectedYear ? "mdp-year-cell--selected" : ""}`}
              onClick={() => {
                changeYearRef.current?.(y);
                closeYearGrid();
              }}
            >
              {y}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const CalendarContainer = ({ className, children }) => {
    const selectedYear = value ? parseCustomDate(value).getFullYear() : new Date().getFullYear();
    return (
      <div className={className} style={{ position: "relative" }}>
        {children}
        {showYearGrid && <YearGrid selectedYear={selectedYear} />}
      </div>
    );
  };

  return (
    <div className="mdp-wrapper">
      <div className="mdp-input-container">
        <DatePicker
          selected={parseCustomDate(value)}
          onChange={onChange}
          maxDate={maxDate}
          minDate={minDate}
          dateFormat={dateFormat}
          placeholderText={placeholder}
          renderCustomHeader={renderCustomHeader}
          calendarClassName={`mdp-calendar${showYearGrid ? " mdp-calendar--year-mode" : ""}`}
          className={`mdp-input ${inputClassName}`}
          calendarContainer={CalendarContainer}
          showPopperArrow={false}
          autoComplete="off"
          onCalendarClose={() => setShowYearGrid(false)}
        />
        <span className="mdp-icon">
          <MdOutlineDateRange />
        </span>
      </div>
    </div>
  );
};

export default ModernDatePicker;
